import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalAgreementsService } from '../../services/portal/portalAgreementsService';
import { useClientPortal } from '../../context/ClientPortalContext';
import { DocumentTextIcon, CheckCircleIcon, ClockIcon, PencilSquareIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const AgreementCard = ({ agreement }) => {
  const getStatusStyle = (status) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'signed') {
      return {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        badge: 'bg-emerald-100 text-emerald-700',
        icon: CheckCircleIcon,
        iconColor: 'text-emerald-600'
      };
    }
    if (normalized === 'sent' || normalized === 'pending') {
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        badge: 'bg-amber-100 text-amber-700',
        icon: ClockIcon,
        iconColor: 'text-amber-600'
      };
    }
    return {
      bg: 'bg-slate-50',
      border: 'border-slate-200',
      badge: 'bg-slate-100 text-slate-600',
      icon: DocumentTextIcon,
      iconColor: 'text-slate-500'
    };
  };

  const style = getStatusStyle(agreement.status);
  const Icon = style.icon;
  const isSigned = agreement.status?.toLowerCase() === 'signed';
  const needsAction = ['sent', 'pending'].includes(agreement.status?.toLowerCase());

  return (
    <div className={`${style.bg} border ${style.border} rounded-2xl p-5 transition-all duration-300 hover:shadow-lg group`}>
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`h-12 w-12 rounded-xl bg-white flex items-center justify-center ${style.iconColor} flex-shrink-0`}>
          <Icon className="h-6 w-6" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-semibold text-slate-900 truncate">
              {agreement.title || 'Agreement'}
            </h3>
            <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${style.badge}`}>
              {agreement.status || 'Draft'}
            </span>
          </div>

          <p className="text-sm text-slate-500 mb-3">
            {isSigned
              ? `Signed on ${agreement.signed_at ? new Date(agreement.signed_at).toLocaleDateString() : 'N/A'}`
              : `Sent ${agreement.sent_at ? new Date(agreement.sent_at).toLocaleDateString() : 'N/A'}`
            }
          </p>

          {/* Action button */}
          {needsAction ? (
            <Link
              to={`/agreements/${agreement.id}/accept`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-sm font-medium hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
            >
              <PencilSquareIcon className="h-4 w-4" />
              Review & Sign
            </Link>
          ) : isSigned ? (
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
              <DocumentTextIcon className="h-4 w-4" />
              View Agreement
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ClientAgreementsPage = () => {
  const { clientId } = useClientPortal();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAgreements = async () => {
    if (!clientId) return;
    setLoading(true);
    const { data } = await PortalAgreementsService.listAgreements(clientId);
    setAgreements(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadAgreements();
  }, [clientId]);

  const pendingAgreements = agreements.filter(a => ['sent', 'pending'].includes(a.status?.toLowerCase()));
  const signedAgreements = agreements.filter(a => a.status?.toLowerCase() === 'signed');

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-20 bg-slate-100 rounded-2xl animate-pulse"></div>
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="h-32 bg-slate-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Agreements</h1>
            <p className="text-sm text-slate-500 mt-0.5">Review and sign your service agreements</p>
          </div>
          <button
            onClick={loadAgreements}
            className="flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Pending Agreements - Action Required */}
      {pendingAgreements.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
              <ClockIcon className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Action Required</h2>
              <p className="text-xs text-slate-500">Please review and sign these agreements</p>
            </div>
          </div>
          <div className="space-y-4">
            {pendingAgreements.map(agreement => (
              <AgreementCard key={agreement.id} agreement={agreement} />
            ))}
          </div>
        </section>
      )}

      {/* Signed Agreements */}
      {signedAgreements.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
              <CheckCircleIcon className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Completed</h2>
              <p className="text-xs text-slate-500">Agreements you've already signed</p>
            </div>
          </div>
          <div className="space-y-4">
            {signedAgreements.map(agreement => (
              <AgreementCard key={agreement.id} agreement={agreement} />
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {agreements.length === 0 && (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
            <DocumentTextIcon className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-1">No agreements yet</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Your service agreements will appear here once they're created by your consultant.
          </p>
        </div>
      )}
    </div>
  );
};

export default ClientAgreementsPage;
